import {
  campaignHasFields,
  getCampaign,
  type CampaignRecord,
} from 'lib/analytics/campaign';
import { isAdClickId } from 'lib/analytics/forwardedParams';
import { isHealthItem, isHealthValue } from 'lib/analytics/healthItems';
import { pixelNameFor, wireNames } from 'lib/analytics/catalog';
import { trackersLive } from 'lib/analytics/destinations';
import { TRACKERS } from 'lib/analytics/trackers';
import { getConsentGate, type ConsentGateState } from 'lib/consent/gate';
import { classifyRoute } from 'lib/consent/policy';

export const ALIAS_EVENTS = new Set([
  'Bottom bar addto cart - CLICK',
  'Rare Bottom bar addto cart - CLICK',
  'ring-air-checkout',
  'ring-pro-checkout',
  'rare-checkout',
  'ringBuyCtaClick',
  'ring_buy_cta_click',
  'ring_buy_add_to_cart',
  'ring_buy_view_item',
  'CLICK: Product Selection, Add to Cart',
]);

const PII_KEY = /email|phone|postcode|postal|zip|address|token|gender|dob|birth/i;

const VOLUME_WINDOW_MS = 1000;

type PixelNames = {
  meta?: string;
  tiktok?: string;
  reddit?: string;
};

const PIXEL_EVENTS: Record<string, PixelNames> = {
  view_item: {
    meta: 'ViewContent',
    tiktok: 'ViewContent',
    reddit: 'ViewContent',
  },
  add_to_cart: {
    meta: 'AddToCart',
    tiktok: 'AddToCart',
    reddit: 'AddToCart',
  },
  begin_checkout: {
    meta: 'InitiateCheckout',
    tiktok: 'InitiateCheckout',
  },
  generate_lead: {
    meta: 'Lead',
  },
};

export type EcommercePayload = {
  currency: string;
  value?: number;
  items: Array<{
    item_id: string;
    item_name?: string;
    price?: number;
    quantity?: number;
  }>;
};

export type EmitInput = {
  name: string;
  props?: Record<string, unknown>;
  ecommerce?: EcommercePayload;
  /** GTM name when it differs from the Vercel snake_case name. */
  gtmName?: string;
  /** Historical names emitted beside `name`. Not sent as properties. */
  legacy?: string | readonly string[];
  pathname?: string;
  now?: number;
};

export type PlannedSinks = {
  vercel: boolean;
  gtm: boolean;
  pixels: string[];
  googleAds: boolean;
};

type VercelSink = (event: string, props?: Record<string, unknown>) => void;

let vercelSink: VercelSink | null = null;
const recent = new Map<string, number>();

export function registerVercelSink(sink: VercelSink): void {
  vercelSink = sink;
}

export function isAliasEvent(name: string): boolean {
  return ALIAS_EVENTS.has(name);
}

function identityOf(
  props: Record<string, unknown> | undefined,
  legacy: string | readonly string[] | undefined,
): string {
  const parts: string[] = [];
  if (typeof legacy === 'string' && legacy) parts.push(legacy);
  else if (Array.isArray(legacy)) {
    for (const name of legacy) {
      if (name) parts.push(name);
    }
  }
  for (const key of ['content_id', 'method', 'reason', 'cta', 'product'] as const) {
    const value = props?.[key];
    if (typeof value === 'string' && value) parts.push(`${key}:${value}`);
  }
  return parts.join('|');
}

export function isDuplicateEvent(
  name: string,
  placement: string | undefined,
  now = Date.now(),
  identity = '',
): boolean {
  const key = `${name}|${placement ?? ''}|${identity}`;
  const previous = recent.get(key);
  if (previous != null && now - previous < VOLUME_WINDOW_MS) return true;
  if (recent.size > 200) {
    for (const [k, timestamp] of recent.entries()) {
      if (now - timestamp >= VOLUME_WINDOW_MS) recent.delete(k);
    }
  }
  recent.set(key, now);
  return false;
}

export function planSinks(name: string, gate: ConsentGateState): PlannedSinks {
  const pixels: string[] = [];
  const mapped = PIXEL_EVENTS[name];
  const ads = gate.stack === 'next' && gate.ads;
  if (ads && mapped?.meta) pixels.push(mapped.meta);
  if (ads && mapped?.tiktok) pixels.push(mapped.tiktok);
  if (ads && mapped?.reddit) pixels.push(mapped.reddit);
  return {
    vercel: gate.stack === 'legacy' || gate.analytics,
    gtm: gate.stack === 'legacy' || gate.gtm,
    pixels,
    googleAds: Boolean(ads && mapped),
  };
}

/**
 * Ad and GTM payloads: no health products, no first-touch page or store.
 * A cart that had a health item loses its value too, since the add-on price
 * would identify it.
 */
function adSafe(
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  props: Record<string, unknown>,
): {
  ecommerce: EcommercePayload | undefined;
  campaign: CampaignRecord | null;
  props: Record<string, unknown>;
} {
  let safeEcommerce = ecommerce;
  if (ecommerce) {
    const items = ecommerce.items.filter((item) => !isHealthItem(item));
    if (items.length < ecommerce.items.length) {
      safeEcommerce = { currency: ecommerce.currency, items };
    }
  }
  const safeCampaign = campaign && { ...campaign };
  if (safeCampaign) {
    delete safeCampaign.landing_path;
    delete safeCampaign.store;
  }
  const safeProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (!isHealthValue(key, value)) safeProps[key] = value;
  }
  return {
    ecommerce: safeEcommerce,
    campaign: safeCampaign && campaignHasFields(safeCampaign) ? safeCampaign : null,
    props: safeProps,
  };
}

function campaignForWire(
  campaign: CampaignRecord | null,
  ads: boolean,
): CampaignRecord | null {
  if (!campaign || !campaignHasFields(campaign)) return null;
  if (ads) return campaign;
  const stripped: CampaignRecord = {};
  for (const [key, value] of Object.entries(campaign)) {
    if (!value || isAdClickId(key)) continue;
    stripped[key as keyof CampaignRecord] = value;
  }
  return campaignHasFields(stripped) ? stripped : null;
}

function placementOf(props: Record<string, unknown> | undefined): string | undefined {
  const placement = props?.placement ?? props?.source;
  return typeof placement === 'string' ? placement : undefined;
}

export function scrubProps(
  props: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!props) return {};
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (PII_KEY.test(key)) continue;
    if (typeof value === 'string') {
      if (value.includes('@') || /[?&](?:email|token)=/i.test(value)) continue;
    }
    clean[key] = value;
  }
  return clean;
}

type ConsentTestWindow = Window & { __UH_CONSENT_TEST__?: boolean | 'queue' };

function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (
    typeof window !== 'undefined' &&
    (window as ConsentTestWindow).__UH_CONSENT_TEST__
  ) {
    return false;
  }
  return (
    (navigator as Navigator & { webdriver?: boolean }).webdriver === true
  );
}

function eventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function scriptReady(id: string): boolean {
  return typeof document !== 'undefined' && Boolean(document.getElementById(id));
}

type PixelWindow = Window & {
  dataLayer?: Record<string, unknown>[];
  fbq?: (...args: unknown[]) => void;
  ttq?: { track?: (event: string, props?: Record<string, unknown>) => void };
  rdt?: (command: string, event: string, props?: Record<string, unknown>) => void;
  gtag?: (...args: unknown[]) => void;
};

function pixelProps(
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): Record<string, unknown> {
  const contentIds = ecommerce?.items.map((item) => item.item_id).filter(Boolean);
  return {
    value: ecommerce?.value,
    currency: ecommerce?.currency,
    content_ids: contentIds,
    content_type: contentIds?.length ? 'product' : undefined,
    event_id: id,
    ...(campaign ?? {}),
  };
}

function sendPixels(
  name: string,
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): void {
  if (typeof window === 'undefined') return;
  const mapped = PIXEL_EVENTS[name];
  if (!mapped) return;
  const props = pixelProps(ecommerce, campaign, id);
  const w = window as PixelWindow;
  if (mapped.meta && scriptReady('uh-meta') && typeof w.fbq === 'function') {
    w.fbq('track', mapped.meta, props, { eventID: id });
  }
  if (mapped.tiktok && scriptReady('uh-tiktok') && w.ttq?.track) {
    w.ttq.track(mapped.tiktok, props);
  }
  if (mapped.reddit && scriptReady('uh-reddit') && typeof w.rdt === 'function') {
    w.rdt('track', mapped.reddit, props);
  }
  if (scriptReady('uh-google-ads') && typeof w.gtag === 'function') {
    w.gtag('event', name, {
      ...props,
      items: ecommerce?.items,
    });
  }
}

function sendDataLayer(
  name: string,
  gtmName: string | undefined,
  props: Record<string, unknown>,
  ecommerce: EcommercePayload | undefined,
  campaign: CampaignRecord | null,
  id: string,
): void {
  if (typeof window === 'undefined') return;
  const w = window as PixelWindow;
  w.dataLayer ??= [];
  const eventName = gtmName ?? name;
  if (ecommerce) {
    w.dataLayer.push({ ecommerce: null });
    w.dataLayer.push({
      event: eventName,
      ecommerce,
      ...props,
      ...(campaign ?? {}),
      event_id: id,
    });
    return;
  }
  w.dataLayer.push({
    event: eventName,
    ...props,
    ...(campaign ?? {}),
    event_id: id,
  });
}

/** A queued event may only reach sinks that were open when it happened and are still open. */
function bothOpen(held: ConsentGateState, live: ConsentGateState): ConsentGateState {
  return {
    stack: live.stack,
    stats: held.stats && live.stats,
    analytics: held.analytics && live.analytics,
    ads: held.ads && live.ads,
    gtm: held.gtm && live.gtm,
    replay: held.replay && live.replay,
    attribution: held.attribution && live.attribution,
  };
}

/**
 * One tap. Historical names stay on the wire beside the canonical name.
 * Pixels fire once, for the canonical ecommerce or lead name. A repeat of
 * the same canonical name and placement inside one second is dropped.
 */
export function emit(input: EmitInput, heldGate?: ConsentGateState): void {
  if (isBot()) return;
  const live = getConsentGate();
  const gate = heldGate ? bothOpen(heldGate, live) : live;
  const props = scrubProps(input.props);
  const pathname =
    input.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  if (classifyRoute(pathname) === 'private') return;

  const names = wireNames(input.name, props, input.legacy);
  if (
    isDuplicateEvent(
      input.name,
      placementOf(props),
      input.now,
      identityOf(props, input.legacy),
    )
  ) {
    return;
  }
  const pixelEvent = pixelNameFor(names);

  const campaign = campaignForWire(
    getCampaign(pathname),
    gate.stack === 'legacy' || gate.ads,
  );
  const id = eventId();
  const thirdParty = trackersLive();
  const forAds = adSafe(input.ecommerce, campaign, props);

  for (const name of names) {
    const planned = planSinks(name, gate);
    const gtmName = name === input.name ? input.gtmName : undefined;
    if (planned.vercel && vercelSink) {
      try {
        vercelSink(name, { ...props, ...(campaign ?? {}), event_id: id });
      } catch (err) {
        console.warn('[analytics:vercel]', err);
      }
    }
    if (planned.gtm && thirdParty) {
      try {
        sendDataLayer(
          name,
          gtmName,
          forAds.props,
          name === input.name ? forAds.ecommerce : undefined,
          forAds.campaign,
          id,
        );
      } catch (err) {
        console.warn('[analytics:gtm]', err);
      }
    }
  }

  // Health pages load GA4 on its own (GTM cannot load there), so the
  // canonical event goes to it directly. Analytics only; no ad tag is loaded.
  if (thirdParty && gate.analytics && scriptReady('uh-ga4')) {
    try {
      (window as PixelWindow).gtag?.('event', input.name, {
        ...props,
        ...(campaign ?? {}),
        ...(input.ecommerce ?? {}),
        send_to: TRACKERS.ga4.id,
      });
    } catch (err) {
      console.warn('[analytics:ga4]', err);
    }
  }

  if (pixelEvent && thirdParty) {
    const planned = planSinks(pixelEvent, gate);
    if (planned.googleAds || planned.pixels.length > 0) {
      try {
        sendPixels(pixelEvent, forAds.ecommerce, forAds.campaign, id);
      } catch (err) {
        console.warn('[analytics:pixels]', err);
      }
    }
  }
}

const queued: Array<{ input: EmitInput; gate: ConsentGateState }> = [];
let flushScheduled = false;
let pageHideBound = false;

function bindPageHideFlush(): void {
  if (pageHideBound || typeof window === 'undefined') return;
  pageHideBound = true;
  window.addEventListener('pagehide', flushAnalyticsQueue);
}

/** `true` skips the idle queue so tests can assert at once; `'queue'` keeps it. */
function consentTestMode(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as ConsentTestWindow).__UH_CONSENT_TEST__ === true
  );
}

/** Click path only enqueues. Delivery runs on idle, or on pagehide if the click navigates away. */
export function enqueueEmit(input: EmitInput): void {
  const pathname =
    input.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const next = pathname ? { ...input, pathname } : input;
  // Playwright asserts dataLayer immediately; skip the idle deferral in tests.
  if (consentTestMode()) {
    emit(next);
    return;
  }
  bindPageHideFlush();
  queued.push({ input: next, gate: getConsentGate() });
  if (flushScheduled) return;
  flushScheduled = true;
  const run = () => {
    flushScheduled = false;
    flushAnalyticsQueue();
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 500 });
  } else {
    setTimeout(run, 0);
  }
}

export function flushAnalyticsQueue(): void {
  flushScheduled = false;
  const batch = queued.splice(0, queued.length);
  for (const item of batch) emit(item.input, item.gate);
}

/** Drop queued emits without sending them (e.g. Decline before reload). */
export function clearAnalyticsQueue(): void {
  queued.splice(0, queued.length);
  flushScheduled = false;
}

export function resetFanoutForTests(): void {
  recent.clear();
  clearAnalyticsQueue();
}
